import * as React from "react";
import { Select, Checkbox, Flex } from "@theorgchartdev/storybook";
import { PaginationConfig } from "@/appComponents/Blocks/Composites/PaginationConfig/PaginationConfig";
import {
  BuildFieldOptions,
  getSubchartHiddenFields,
  SUBCHARTS_TEST_PREFIX
} from "./SubchartConfigUtils";
import { returnHeadersListNoFormulas } from "@/Utils/DataLib";

import SectionCard, { SectionCardGroup } from "../../../../Composites/SectionCard/SectionCard";
import FieldSelector from "../ImportFiltersConfiguration/FieldSelector";
import * as StateLib from "@/Utils/StateLib";
import { appstr } from "@coreapp/utils/appstr";
import { getFeatureAccess } from "@/Utils/Misc";
import { enumFeaturesList, enumObjectTypes } from "@coreapp/constants";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";

export const SubchartConfiguration = ({ chartConfig, handleUpdate }) => {
  if (!chartConfig) return;
  let wsMgr = StateLib.GetWSMgr();
  const eventTracker = wsMgr.GetUsageTracker();

  React.useEffect(() => {
    eventTracker.ReportEvent("chart_settings.subchart.opened");
  }, []);

  let showShortcuts = false;

  const subchartInfo = chartConfig.subchartrule;
  const filteredHeaders = returnHeadersListNoFormulas(chartConfig, true);
  const isPositionChart = IsPositionChart(chartConfig);
  const subchartHiddenFields = getSubchartHiddenFields(
    chartConfig,
    isPositionChart ? enumObjectTypes.position : undefined
  );
  const positionFieldTypeFallback = isPositionChart ? enumObjectTypes.position : undefined;

  const shortCutInfo = chartConfig.shortcutConfig
    ? chartConfig.shortcutConfig
    : { field: "Shortcut", enabled: false };

  const scSortInfo = chartConfig.scSort
    ? chartConfig.scSort
    : { enabled: false, scSort: "", sortKey: "" };

  const ChangeSubchartRuleField = (key, value) => {
    if (key === "scChartBreakType") value = parseInt(value);
    chartConfig.subchartrule = { ...chartConfig.subchartrule, [key]: value };
    handleUpdate(chartConfig, "subchartrule");
  };

  const HandleCheckboxToggle = id => {
    switch (id) {
      case "sort-enable":
        let newScSort = chartConfig.scSort ? chartConfig.scSort : scSortInfo;
        newScSort.enabled = !newScSort.enabled;
        chartConfig.scSort = newScSort;
        handleUpdate(chartConfig, "scSort");
        break;
    }
  };

  const ChangeNameField = (_, fieldValue) => {
    chartConfig.subchartrule = {
      ...chartConfig.subchartrule,
      nameusing: fieldValue.fieldName,
      nameusingFieldType: fieldValue.fieldType
    };
    handleUpdate(chartConfig, "subchartrule");
  };

  const ChangeSubchartSortField = (_, fieldValue) => {
    chartConfig.scSort = {
      ...chartConfig.scSort,
      sortKey: fieldValue.fieldName,
      sortKeyType: fieldValue.fieldType
    };
    handleUpdate(chartConfig, "scSort");
  };

  const ChangeShortcutField = event => {
    handleUpdate(chartConfig => {
      const prevShortcutConfig = chartConfig.shortcutConfig;
      return {
        ...chartConfig,
        shortcutConfig: { ...prevShortcutConfig, field: event.value }
      };
    });
  };

  return (
    <SectionCardGroup>
      <SectionCard title={appstr("Subcharts")}>
        <Flex direction={"column"} gap={"8px"} width={"400px"}>
          <FieldSelector
            chartConfig={chartConfig}
            onClick={ChangeNameField}
            fieldName={subchartInfo.nameusing}
            fieldType={subchartInfo.nameusingFieldType ?? positionFieldTypeFallback}
            onlyFromType={isPositionChart ? enumObjectTypes.position : undefined}
            filterFields={subchartHiddenFields}
            disabled={!getFeatureAccess(enumFeaturesList.subchartNaming)}
            containerProps={{
              id: "sc-name-select",
              dataTestId: `${SUBCHARTS_TEST_PREFIX}-name-field-select`,
              label: appstr("Subchart name field"),
              selectSize: "medium",
              listProps: {
                enableSearch: true
              }
            }}
          />
        </Flex>
      </SectionCard>
      <SectionCard title={appstr("Subchart sorting")}>
        <Flex gap={"16px"} direction={"column"} width={"400px"}>
          <Flex direction={"column"} gap={"8px"}>
            <Checkbox
              id="sort-enable"
              data-testid={`${SUBCHARTS_TEST_PREFIX}-enable-sort-checkbox`}
              size="medium"
              checked={scSortInfo.enabled}
              label={appstr("Enable sort")}
              onToggle={() => HandleCheckboxToggle("sort-enable")}
              disabled={!getFeatureAccess(enumFeaturesList.subchartSorting)}
            />
            <FieldSelector
              chartConfig={chartConfig}
              onClick={ChangeSubchartSortField}
              fieldName={scSortInfo.sortKey}
              fieldType={scSortInfo.sortKeyType ?? positionFieldTypeFallback}
              onlyFromType={isPositionChart ? enumObjectTypes.position : undefined}
              filterFields={subchartHiddenFields}
              disabled={!scSortInfo.enabled}
              containerProps={{
                dataTestId: `${SUBCHARTS_TEST_PREFIX}-sort-field-select`,
                selectSize: "medium",
                listProps: {
                  enableSearch: true
                }
              }}
            />
          </Flex>
        </Flex>
      </SectionCard>
      <SectionCard title={appstr("Subchart breaks")}>
        <Flex>
          <PaginationConfig
            fieldNames={filteredHeaders}
            scConfig={subchartInfo}
            handleUpdate={ChangeSubchartRuleField}
            useSettingModalStyle={true}
            showNameUsing={false}
          />
        </Flex>
      </SectionCard>
      {showShortcuts && (
        <SectionCard title={"Shortcuts"}>
          <div style={{ display: "flex", flexDirection: "row" }}>
            <Checkbox
              id="shortcut-enable"
              size="small"
              checked={shortCutInfo.enabled}
              onToggle={() => HandleCheckboxToggle("shortcut-enable")}
            />
            <Select
              inputOnly={false}
              label={appstr("Shortcut Field") + ": "}
              value={shortCutInfo.field}
              options={BuildFieldOptions(chartConfig.headers, ChangeShortcutField)}
              size="small"
              disabled={!shortCutInfo.enabled}
            />
          </div>
        </SectionCard>
      )}
    </SectionCardGroup>
  );
};
